import { useContext, useEffect, useState } from "react";
import { UserStore } from "../..";
import BreadCrumbs from "../../components/breadcrumsb";
import { PageContainer } from "../../components/page";
import {
  getKioskAttractors,
  updateKioskAttractorTimer,
} from "../../api/new-api";
import { PEProgressBar } from "../../components/linear-progress";
import { ErrorAlert } from "../../components/alerts";
import moment from "moment";
import { runAuthHandler } from "../util";
import { useNavigate } from "react-router";
import { KioskAttractorsResponse } from "../../types";
import MultipleAttractorsContainer from "./MultipleAttractorsContainer";
import { observer } from "mobx-react";
import { CloudArrowUpIcon } from "@heroicons/react/24/solid";
import SimpleSecondsContainer from "../../components/SimpleSecondsContainer";
import KioskAttractorDisplayPreview from "../../components/KioskAttractorDisplayPreview";

const fetchData = async ({
  setData,
  setLoading,
  setError,
  setIntervalSeconds,
  setValueIntervalSeconds,
}: {
  setData: any;
  setLoading: any;
  setError: any;
  setIntervalSeconds: any;
  setValueIntervalSeconds: any;
}) => {
  setError("");
  setLoading(true);
  try {
    const res = await getKioskAttractors();
    if (res.data) {
      setData(res.data.attractors);

      setIntervalSeconds({
        timeToChangeAttractor: res.data.timeToChangeAttractor,
        timeUntilBackgroundMode: res.data.timeUntilBackgroundMode,
        timeToRefreshBackendData: res.data.timeToRefreshBackendData,
      });

      setValueIntervalSeconds({
        timeToChangeAttractor: res.data.timeToChangeAttractor,
        timeUntilBackgroundMode: res.data.timeUntilBackgroundMode,
        timeToRefreshBackendData: res.data.timeToRefreshBackendData,
      });
    }
  } catch (error) {
    setError("Error getting kiosk attractors");
  } finally {
    setLoading(false);
  }
};

const PAGE_TITLE = "Kiosk Attractor";

const KioskAttractorPage = () => {
  const navigate = useNavigate();
  const userStore = useContext(UserStore);
  const { selectedVenue } = userStore;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>("");
  const [data, setData] = useState<any>([]);
  const [weekCount, setWeekCount] = useState<number>(0);
  const [allCount, setAllCount] = useState<number>(0);
  const [expiredCount, setExpiredCount] = useState<number>(0);
  const [upcomingCount, setUpcomingCount] = useState<number>(0); // New state for upcoming count
  const [currentAttractors, setCurrentAttractors] = useState<
    KioskAttractorsResponse[]
  >([]);
  const [filteredData, setFilteredData] = useState<KioskAttractorsResponse[]>(
    []
  );
  const [activeFilter, setActiveFilter] = useState<
    "week" | "all" | "expired" | "upcoming" // Updated to include "upcoming"
  >("week");

  const [intervalSeconds, setIntervalSeconds] = useState({
    timeToChangeAttractor: 0,
    timeUntilBackgroundMode: 0,
    timeToRefreshBackendData: 0,
  });

  const [valueIntervalSeconds, setValueIntervalSeconds] = useState({
    timeToChangeAttractor: 0,
    timeUntilBackgroundMode: 0,
    timeToRefreshBackendData: 0,
  });
  const [editableFields, setEditableFields] = useState({
    timeToChangeAttractor: false,
    timeUntilBackgroundMode: false,
    timeToRefreshBackendData: false,
  });

  const handleSaveIntervalSeconds = async (
    field: keyof typeof intervalSeconds
  ) => {
    const data = {
      KioskTimeUntilBackgroundModeSecs:
        valueIntervalSeconds.timeUntilBackgroundMode,
      KioskTimeToRefreshBackendDataSecs:
        valueIntervalSeconds.timeToRefreshBackendData,
      KioskTimeToChangeAttractorSecs:
        valueIntervalSeconds.timeToChangeAttractor,
    };

    if (field === "timeToChangeAttractor") {
      data.KioskTimeToChangeAttractorSecs =
        intervalSeconds.timeToChangeAttractor;
    }
    if (field === "timeUntilBackgroundMode") {
      data.KioskTimeUntilBackgroundModeSecs =
        intervalSeconds.timeUntilBackgroundMode;
    }
    if (field === "timeToRefreshBackendData") {
      data.KioskTimeToRefreshBackendDataSecs =
        intervalSeconds.timeToRefreshBackendData;
    }

    try {
      const res = await updateKioskAttractorTimer(data);

      if (res.data.success) {
        alert("Successfully updated");
        setEditableFields((prev) => ({ ...prev, [field]: false }));
        setValueIntervalSeconds((prev) => ({
          ...prev,
          [field]: intervalSeconds[field],
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const currentDayOfWeek = moment().format("ddd").toUpperCase();
  const currentTime = moment().valueOf();

  const transformAttractorData = () => {
    if (data.length === 0) {
      setCurrentAttractors([]);
      setFilteredData([]);
      setWeekCount(0);
      setAllCount(0);
      setExpiredCount(0);
      setUpcomingCount(0); // Reset upcoming count
      return;
    }

    // Current attractors (Showing Today)
    const currentAttractors = data.filter(
      (banner: {
        dayVisibility: string | string[] | undefined;
        startDateEpoch: number | undefined;
        endDateEpoch: number | undefined;
      }) => {
        return (
          banner?.dayVisibility != undefined &&
          banner?.startDateEpoch != undefined &&
          banner?.endDateEpoch != undefined &&
          banner?.dayVisibility?.includes(currentDayOfWeek) &&
          currentTime >= banner?.startDateEpoch &&
          currentTime <= banner?.endDateEpoch
        );
      }
    );

    // Filter calculations
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);

    const weekBanners = data.filter((banner: { timeCreatedAt: number }) => {
      const bannerDate = new Date(banner.timeCreatedAt);
      return bannerDate >= weekAgo;
    });
    const expiredBanners = data.filter(
      (banner: { endDateEpoch: number | undefined }) => {
        const endDate = banner.endDateEpoch
          ? new Date(banner.endDateEpoch)
          : null;
        return endDate && endDate < today;
      }
    );
    const upcomingBanners = data.filter(
      (banner: { startDateEpoch: number | undefined }) => {
        const startDate = banner.startDateEpoch
          ? new Date(banner.startDateEpoch)
          : null;
        return startDate && startDate > today; // Upcoming: start date is in the future
      }
    );

    setWeekCount(weekBanners.length);
    setAllCount(data.length);
    setExpiredCount(expiredBanners.length);
    setUpcomingCount(upcomingBanners.length); // Set upcoming count

    // Set filtered data based on active filter
    if (activeFilter === "week") {
      setFilteredData(weekBanners);
    } else if (activeFilter === "all") {
      setFilteredData(data);
    } else if (activeFilter === "expired") {
      setFilteredData(expiredBanners);
    } else if (activeFilter === "upcoming") {
      setFilteredData(upcomingBanners); // Handle upcoming filter
    }

    setCurrentAttractors(currentAttractors);
  };

  const getAttractors = async () => {
    fetchData({
      setData,
      setLoading,
      setError,
      setIntervalSeconds,
      setValueIntervalSeconds,
    });
  };

  useEffect(() => {
    transformAttractorData();
  }, [data, activeFilter]);

  useEffect(() => {
    setCurrentAttractors([]);
    setFilteredData([]);
    setWeekCount(0);
    setAllCount(0);
    setExpiredCount(0);
    setUpcomingCount(0); // Reset upcoming count on venue change
    setLoading(true);
    runAuthHandler({ userStore, navigate });
    getAttractors();
  }, [selectedVenue]);

  return (
    <PageContainer>
      <BreadCrumbs
        routes={[
          { path: "/home", breadcrumbName: "Home" },
          { path: "/kiosk-attractors", breadcrumbName: "Kiosk Attractor" },
        ]}
      />
      <div className="text-3xl mb-6 text-center font-bold">{PAGE_TITLE}</div>

      {loading && <PEProgressBar />}
      {error && <ErrorAlert message={error} />}

      {!loading && (
        <div className="my-5">
          <div
            className={
              "flex bg-slate-100 p-10 items-start justify-center gap-20"
            }
          >
            <KioskAttractorDisplayPreview
              currentAttractors={currentAttractors}
            />

            <div
              onClick={() => {
                navigate("/kiosk-attractors/create");
              }}
              className={
                "border-4 p-10 text-center hover:opacity-75 hover:cursor-pointer"
              }
            >
              <div className="flex flex-col items-center gap-1 h-max">
                <p className={"text-md font-bold"}>
                  Click here to start adding Kiosk Attractor
                </p>

                <p className="text-md font-light">upload specs:</p>
                <p className="text-md font-light">
                  Thumbnail: Width to Height Ratio of 2:1
                </p>

                <CloudArrowUpIcon className={"h-10 w-10 text-blue-500"} />
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <div>
          <SimpleSecondsContainer
            label="Time to Change Attractor"
            initialValue={valueIntervalSeconds.timeToChangeAttractor}
            value={intervalSeconds.timeToChangeAttractor}
            setValue={(v: any) =>
              setIntervalSeconds({
                ...intervalSeconds,
                timeToChangeAttractor: v,
              })
            }
            editable={editableFields.timeToChangeAttractor}
            onEdit={(v: any) =>
              setEditableFields((prev) => ({
                ...prev,
                timeToChangeAttractor: v,
              }))
            }
            onSave={() => handleSaveIntervalSeconds("timeToChangeAttractor")}
            onCancel={() => {
              setEditableFields((prev) => ({
                ...prev,
                timeToChangeAttractor: false,
              }));
              setIntervalSeconds((prev) => ({
                ...prev,
                timeToChangeAttractor:
                  valueIntervalSeconds.timeToChangeAttractor,
              }));
            }}
          />
          <SimpleSecondsContainer
            label="Time Until Background Mode"
            initialValue={valueIntervalSeconds.timeUntilBackgroundMode}
            value={intervalSeconds.timeUntilBackgroundMode}
            setValue={(v: any) =>
              setIntervalSeconds({
                ...intervalSeconds,
                timeUntilBackgroundMode: v,
              })
            }
            editable={editableFields.timeUntilBackgroundMode}
            onEdit={(v: any) =>
              setEditableFields((prev) => ({
                ...prev,
                timeUntilBackgroundMode: v,
              }))
            }
            onSave={() => handleSaveIntervalSeconds("timeUntilBackgroundMode")}
            onCancel={() => {
              setEditableFields((prev) => ({
                ...prev,
                timeUntilBackgroundMode: false,
              }));
              setIntervalSeconds((prev) => ({
                ...prev,
                timeUntilBackgroundMode:
                  valueIntervalSeconds.timeUntilBackgroundMode,
              }));
            }}
          />

          <SimpleSecondsContainer
            label="Time to Refresh Backend Data"
            initialValue={valueIntervalSeconds.timeToRefreshBackendData}
            value={intervalSeconds.timeToRefreshBackendData}
            setValue={(v: any) =>
              setIntervalSeconds({
                ...intervalSeconds,
                timeToRefreshBackendData: v,
              })
            }
            editable={editableFields.timeToRefreshBackendData}
            onEdit={(v: any) =>
              setEditableFields((prev) => ({
                ...prev,
                timeToRefreshBackendData: v,
              }))
            }
            onSave={() => handleSaveIntervalSeconds("timeToRefreshBackendData")}
            onCancel={() => {
              setEditableFields((prev) => ({
                ...prev,
                timeToRefreshBackendData: false,
              }));
              setIntervalSeconds((prev) => ({
                ...prev,
                timeToRefreshBackendData:
                  valueIntervalSeconds.timeToRefreshBackendData,
              }));
            }}
          />
        </div>
      )}
      {data?.length > 0 && (
        <div className="w-full">
          <div className="block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
            <h5 className="text-center mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Showing Today
            </h5>
            <p className="text-center font-normal text-gray-700 dark:text-gray-400">
              Here are the current attractors:
            </p>

            <div className="mt-4">
              <MultipleAttractorsContainer
                errorMessage="No current attractors found"
                data={currentAttractors as KioskAttractorsResponse[]}
                getAttractors={getAttractors}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-center gap-4 mb-6">
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "week"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("week")}
            >
              Showing This Week ({weekCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("all")}
            >
              All Attractors ({allCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "expired"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("expired")}
            >
              Expired Attractors ({expiredCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "upcoming"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("upcoming")}
            >
              Upcoming Attractors ({upcomingCount}) {/* New Upcoming button */}
            </button>
          </div>

          <div className="block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
            <h5 className="text-center mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {activeFilter === "week"
                ? "Showing This Week"
                : activeFilter === "all"
                ? "All Attractors"
                : activeFilter === "expired"
                ? "Expired Attractors"
                : "Upcoming Attractors"}{" "}
              {/* Updated title */}
            </h5>
            <p className="text-center font-normal text-gray-700 dark:text-gray-400">
              {activeFilter === "week"
                ? "Attractors from the last 7 days"
                : activeFilter === "all"
                ? "All showing and finished attractors"
                : activeFilter === "expired"
                ? "Finished attractors"
                : "Attractors scheduled to start in the future"}{" "}
              {/* Updated description */}
            </p>

            <div className="mt-4">
              <MultipleAttractorsContainer
                errorMessage={`No ${
                  activeFilter === "week"
                    ? "current"
                    : activeFilter === "all"
                    ? "other"
                    : activeFilter === "expired"
                    ? "expired"
                    : "upcoming" // Updated error message
                } attractors found`}
                data={filteredData as KioskAttractorsResponse[]}
                getAttractors={getAttractors}
              />
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default observer(KioskAttractorPage);
